/**
 * Générateur — Automatisme : lexique et codage de la géométrie plane
 * Attendu ID : 1015 | Niveau : 6ème | Domaine : GM
 * Rubrique : AUTOMATISME (programme 2025) — « L'élève connaît le lexique et
 * le codage des objets de base de la géométrie plane : angle droit, égalité
 * de longueurs, égalité d'angles. »
 * Labels (cartes exerciseur) :
 *   - Les codages : angle droit, longueurs, angles
 *
 * ── Plan de cartes (2026-08-22, cadrage prof, v2 figures variées) ──────────
 * TROIS codages fermés (petit carré, traits, arcs) + le lexique. NOTATIONS
 * écartées (arbitrage prof). On DÉCODE (coder = l'automatisme voisin).
 * FIGURES VARIÉES (demande prof : c'est la variété qui teste la LECTURE du
 * codage, pas la reconnaissance d'une forme apprise) : triangle rectangle,
 * triangle isocèle, rectangle, losange, trapèze rectangle, pentagone
 * quelconque à deux côtés égaux — toutes tournées dans tous les sens.
 * V/F retiré (arbitrage prof : le codage se lit sur des figures).
 * VERROU A1 : chaque figure ne porte que le codage interrogé, et chaque
 * distracteur est FACTUELLEMENT FAUX sur la figure — d'où des jeux de
 * distracteurs PAR FIGURE (dans un rectangle « les angles en A et C sont
 * égaux » est vrai ; dans un losange « les côtés opposés sont parallèles »
 * aussi : jamais servis là où ils sont vrais).
 * COHÉRENCE dessin ↔ codage par construction (égalités exactes, angles
 * droits exacts, non-codés différant d'au moins ~15-20 %).
 */
import {
    randomChoice, randInt, pickLettresSains,
    makeQcm,
} from '../../../utils';

const ATTENDU_ID = 1015;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Les codages : angle droit, longueurs, angles';

const garde = (condition, message) => {
    if (!condition) throw new Error(`gen_1015 : ${message}`);
};

const dataUrl = (svg) => `data:image/svg+xml,${encodeURIComponent(svg)}`;
const elImage = (url) => ({ version: 1, elements: [{ type: 'image', config: { imageUrl: url } }] });

const BLEU = '#1E3A8A';
const ROUGE = '#dc2626';
const LETTRES_POOL = 'ABCDEFGHKLMNPRST'.split('');

// ─── Géométrie ────────────────────────────────────────────────────────────

const rot = (angleDeg) => {
    const a = (angleDeg * Math.PI) / 180;
    return ([x, y]) => [x * Math.cos(a) - y * Math.sin(a), x * Math.sin(a) + y * Math.cos(a)];
};
const dist = (P, Q) => Math.hypot(P[0] - Q[0], P[1] - Q[1]);
const unit = (P, Q) => {
    const d = dist(P, Q);
    return [(Q[0] - P[0]) / d, (Q[1] - P[1]) / d];
};

/**
 * Constructeurs de figures (coordonnées locales EXACTES).
 */
const FIGURES = {
    triangle_rect: () => {
        const a = randInt(70, 95);
        const b = Math.round(a * (1.3 + Math.random() * 0.4));
        return { pts: [[0, -a], [0, 0], [b, 0]], droitEn: [1] };
    },
    triangle_iso: () => {
        for (let essai = 0; essai < 30; essai++) {
            const base = randInt(80, 110);
            const h = Math.round(base * (0.75 + Math.random() * 0.5));
            const pts = [[base / 2, -h], [0, 0], [base, 0]];
            const cote = dist(pts[0], pts[1]);
            if (Math.abs(base - cote) < 0.2 * cote) continue;
            return { pts, apex: 0 };
        }
        return { pts: [[50, -95], [0, 0], [100, 0]], apex: 0 };
    },
    rectangle: () => {
        const h = randInt(55, 75);
        const w = Math.round(h * (1.35 + Math.random() * 0.45));
        return { pts: [[0, -h], [0, 0], [w, 0], [w, -h]], droitEn: [0, 1, 2, 3] };
    },
    losange: () => {
        const p = randInt(55, 70);
        const q = Math.round(p * (0.55 + Math.random() * 0.2));
        // p > q : les sommets sur la grande diagonale (0, 2) portent les aigus
        return { pts: [[-p, 0], [0, -q], [p, 0], [0, q]], aigusEn: [0, 2], obtusEn: [1, 3] };
    },
    trapeze_rect: () => {
        for (let essai = 0; essai < 30; essai++) {
            const h = randInt(55, 75);
            const w1 = Math.round(h * (1.4 + Math.random() * 0.4));
            const w2 = Math.round(w1 * (0.5 + Math.random() * 0.2));
            // w2 ≉ h : sinon « les côtés en B ont la même longueur » deviendrait vrai
            if (Math.abs(w2 - h) < 0.15 * h) continue;
            return { pts: [[0, 0], [0, -h], [w2, -h], [w1, 0]], droitEn: [0, 1] };
        }
        return { pts: [[0, 0], [0, -70], [55, -70], [110, 0]], droitEn: [0, 1] };
    },
    pentagone: () => {
        for (let essai = 0; essai < 40; essai++) {
            const w = randInt(70, 95);
            const d = randInt(24, 38);
            const h1 = randInt(40, 60);
            const h2 = h1 + randInt(30, 45);
            const j = randomChoice([-1, 1]) * randInt(10, 20);
            // A B C D E : BC = EA par symétrie ; D décalé pour casser CD = DE
            const pts = [[0, 0], [w, 0], [w + d, -h1], [w / 2 + j, -h2], [-d, -h1]];
            const cd = dist(pts[2], pts[3]);
            const de = dist(pts[3], pts[4]);
            if (Math.abs(cd - de) < 0.15 * Math.max(cd, de)) continue;
            const bc = dist(pts[1], pts[2]);
            if (Math.abs(w - bc) < 0.15 * bc) continue;
            // AB ≉ CD : la paire-distracteur « AB et CD » doit rester visiblement fausse
            if (Math.abs(w - cd) < 0.15 * Math.max(w, cd)) continue;
            // pas d'angle quasi droit en A ni en B (distracteurs sûrs)
            const cosEn = (i, a, b) => {
                const u = unit(pts[i], pts[a]);
                const v = unit(pts[i], pts[b]);
                return Math.abs(u[0] * v[0] + u[1] * v[1]);
            };
            if (cosEn(1, 0, 2) < 0.12 || cosEn(0, 1, 4) < 0.12) continue;
            garde(Math.abs(dist(pts[1], pts[2]) - dist(pts[4], pts[0])) < 1e-9, 'symétrie pentagone');
            return { pts };
        }
        return { pts: [[0, 0], [70, 0], [98, -50], [25, -90], [-28, -50]] };
    },
};

/** Rotation quelconque + recadrage (étiquettes comprises). */
const poserFigure = (pts) => {
    const r = rot(randInt(0, 359));
    let tourne = pts.map(r);
    const marge = 34;
    const dx = marge - Math.min(...tourne.map((p) => p[0]));
    const dy = marge - Math.min(...tourne.map((p) => p[1]));
    tourne = tourne.map(([x, y]) => [x + dx, y + dy]);
    const W = Math.ceil(Math.max(...tourne.map((p) => p[0])) + marge);
    const H = Math.ceil(Math.max(...tourne.map((p) => p[1])) + marge);
    return { pts: tourne, W, H };
};

// ─── Les codages dessinés ─────────────────────────────────────────────────

const svgCarre = (S, U, V, sommet) => {
    const u = unit(S, U);
    const v = unit(S, V);
    const s = 12;
    const p1 = [S[0] + s * u[0], S[1] + s * u[1]];
    const p2 = [S[0] + s * (u[0] + v[0]), S[1] + s * (u[1] + v[1])];
    const p3 = [S[0] + s * v[0], S[1] + s * v[1]];
    return `<path d="M ${p1[0].toFixed(1)} ${p1[1].toFixed(1)} L ${p2[0].toFixed(1)} ${p2[1].toFixed(1)} L ${p3[0].toFixed(1)} ${p3[1].toFixed(1)}" fill="none" stroke="${ROUGE}" stroke-width="1.8" data-code="carre" data-sommet="${sommet}"/>`;
};

const svgTicks = (P, Q, n, cote) => {
    const u = unit(P, Q);
    const nx = -u[1];
    const ny = u[0];
    const mx = (P[0] + Q[0]) / 2;
    const my = (P[1] + Q[1]) / 2;
    const out = [];
    for (let i = 0; i < n; i++) {
        const off = (i - (n - 1) / 2) * 5;
        const cx = mx + off * u[0];
        const cy = my + off * u[1];
        out.push(`<line x1="${(cx - 6 * nx).toFixed(1)}" y1="${(cy - 6 * ny).toFixed(1)}" x2="${(cx + 6 * nx).toFixed(1)}" y2="${(cy + 6 * ny).toFixed(1)}" stroke="${ROUGE}" stroke-width="2" data-code="tick" data-cote="${cote}" data-n="${n}"/>`);
    }
    return out.join('\n  ');
};

const svgArcs = (S, U, V, n, sommet) => {
    const u = unit(S, U);
    const v = unit(S, V);
    const out = [];
    for (let i = 0; i < n; i++) {
        const r = 14 + i * 5;
        const p1 = [S[0] + r * u[0], S[1] + r * u[1]];
        const p2 = [S[0] + r * v[0], S[1] + r * v[1]];
        const cross = u[0] * v[1] - u[1] * v[0];
        const sweep = cross > 0 ? 1 : 0;
        out.push(`<path d="M ${p1[0].toFixed(1)} ${p1[1].toFixed(1)} A ${r} ${r} 0 0 ${sweep} ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}" fill="none" stroke="${ROUGE}" stroke-width="1.6" data-code="arc" data-sommet="${sommet}" data-n="${n}"/>`);
    }
    return out.join('\n  ');
};

/**
 * La figure complète. `codes` (en INDICES de sommets) :
 *   { carres: [i], ticks: [{ paires: [[i, j]], n }], arcs: [{ sommets: [i], n }] }
 */
const svgFigure = (pts0, lettres, codes) => {
    const { pts, W, H } = poserFigure(pts0);
    const n = pts.length;
    const centre = pts.reduce(([sx, sy], [x, y]) => [sx + x / n, sy + y / n], [0, 0]);
    const p = [];
    p.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${Math.min(300, W)}" role="img"`);
    p.push(`  aria-label="Une figure ${lettres.join('')} avec des codages géométriques.">`);
    p.push(`  <polygon points="${pts.map((pt) => `${pt[0].toFixed(1)},${pt[1].toFixed(1)}`).join(' ')}" fill="#eff6ff" stroke="${BLEU}" stroke-width="2.2"/>`);
    const morceaux = [];
    (codes.carres || []).forEach((i) => {
        morceaux.push(svgCarre(pts[i], pts[(i + n - 1) % n], pts[(i + 1) % n], lettres[i]));
    });
    (codes.ticks || []).forEach((g) => {
        g.paires.forEach(([i, j]) => {
            morceaux.push(svgTicks(pts[i], pts[j], g.n, `${lettres[i]}${lettres[j]}`));
        });
    });
    (codes.arcs || []).forEach((g) => {
        g.sommets.forEach((i) => {
            morceaux.push(svgArcs(pts[i], pts[(i + n - 1) % n], pts[(i + 1) % n], g.n, lettres[i]));
        });
    });
    p.push('  ' + morceaux.join('\n  '));
    pts.forEach((pt, i) => {
        const ux = pt[0] - centre[0];
        const uy = pt[1] - centre[1];
        const d = Math.hypot(ux, uy);
        const lx = pt[0] + (ux / d) * 17;
        const ly = pt[1] + (uy / d) * 17;
        p.push(`  <text x="${lx.toFixed(1)}" y="${(ly + 5).toFixed(1)}" font-size="15" font-weight="700" fill="#111827" text-anchor="middle" font-family="system-ui,sans-serif" data-sommet="${lettres[i]}" data-i="${i}" data-x="${pt[0].toFixed(1)}" data-y="${pt[1].toFixed(1)}">${lettres[i]}</text>`);
    });
    p.push('</svg>');
    return p.join('\n');
};

// ─── Assemblage QCM ───────────────────────────────────────────────────────

const finirQcm = (bonne, dis, texte, sousType, elements) => {
    const payload = makeQcm(
        [bonne, ...dis.map((c) => c.valeur)],
        bonne,
        texte,
        { ...opts(L1), ...(elements ? { elements_additionnels: elements } : {}) },
        false,
    );
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, distracteurs: dis };
    return payload;
};

// ─── Format 1 : le SENS du codage (figure à codage UNIQUE) ────────────────

const genSensQcm = () => {
    const focal = randomChoice(['carre', 'ticks', 'arcs']);
    if (focal === 'carre') {
        const forme = randomChoice(['triangle_rect', 'rectangle', 'trapeze_rect']);
        const fig = FIGURES[forme]();
        const L = pickLettresSains(LETTRES_POOL, fig.pts.length);
        const nb = fig.pts.length;
        const iC = randomChoice(fig.droitEn);
        const v1 = L[(iC + nb - 1) % nb];
        const v2 = L[(iC + 1) % nb];
        const dis = [
            { valeur: `les côtés [${L[iC]}${v1}] et [${L[iC]}${v2}] ont la même longueur`, code: 'confusion_ticks' },
            { valeur: `${L[iC]} est le milieu du côté [${v1}${v2}]`, code: 'confusion_milieu' },
            forme === 'triangle_rect'
                ? { valeur: `les angles en ${v1} et en ${v2} sont égaux`, code: 'confusion_arcs' }
                : { valeur: `les côtés [${L[iC]}${v1}] et [${L[iC]}${v2}] sont parallèles`, code: 'confusion_parallele' },
        ];
        const svg = svgFigure(fig.pts, L, { carres: fig.droitEn });
        return finirQcm(`il y a un angle droit en ${L[iC]}`, dis,
            `<p><b>Sur la figure, que signifie le petit carré en ${L[iC]} ?</b></p><p>{{element:0}}</p>`,
            `sens_carre_${forme}`, elImage(dataUrl(svg)));
    }
    if (focal === 'ticks') {
        const forme = randomChoice(['triangle_iso', 'losange', 'pentagone']);
        const fig = FIGURES[forme]();
        const L = pickLettresSains(LETTRES_POOL, fig.pts.length);
        if (forme === 'losange') {
            const svg = svgFigure(fig.pts, L, { ticks: [{ paires: [[0, 1], [1, 2], [2, 3], [3, 0]], n: 1 }] });
            const dis = [
                { valeur: 'les quatre angles de la figure sont égaux', code: 'confusion_arcs' },
                { valeur: 'la figure a quatre angles droits', code: 'confusion_carre' },
                { valeur: 'les diagonales ont la même longueur', code: 'confusion_diagonales' },
            ];
            return finirQcm('les quatre côtés ont la même longueur', dis,
                '<p><b>Que signifient les petits traits identiques sur les quatre côtés ?</b></p><p>{{element:0}}</p>',
                'sens_ticks_losange', elImage(dataUrl(svg)));
        }
        const paires = forme === 'triangle_iso' ? [[0, 1], [0, 2]] : [[1, 2], [4, 0]];
        const n = randomChoice([1, 2]);
        const [c1, c2] = paires.map(([i, j]) => `[${L[i]}${L[j]}]`);
        const svg = svgFigure(fig.pts, L, { ticks: [{ paires, n }] });
        const sBase = forme === 'triangle_iso' ? L[0] : L[1];
        const dis = [
            { valeur: `il y a un angle droit en ${sBase}`, code: 'confusion_carre' },
            { valeur: `les côtés ${c1} et ${c2} sont parallèles`, code: 'confusion_parallele' },
            { valeur: `${sBase} est le milieu d'un côté`, code: 'confusion_milieu' },
        ];
        return finirQcm(`les côtés ${c1} et ${c2} ont la même longueur`, dis,
            `<p><b>Que signifient les petits traits identiques sur les côtés ${c1} et ${c2} ?</b></p><p>{{element:0}}</p>`,
            `sens_ticks_${forme}`, elImage(dataUrl(svg)));
    }
    // arcs
    const forme = randomChoice(['triangle_iso', 'losange']);
    const fig = FIGURES[forme]();
    const L = pickLettresSains(LETTRES_POOL, fig.pts.length);
    if (forme === 'losange') {
        const svg = svgFigure(fig.pts, L, {
            arcs: [{ sommets: fig.aigusEn, n: 1 }, { sommets: fig.obtusEn, n: 2 }],
        });
        const [a1, a2] = fig.aigusEn.map((i) => L[i]);
        const dis = [
            { valeur: `il y a un angle droit en ${a1} et en ${a2}`, code: 'confusion_carre' },
            { valeur: 'tous les angles du losange sont égaux', code: 'confusion_tous_angles' },
            { valeur: `${a1} et ${a2} sont les milieux des diagonales`, code: 'confusion_milieu' },
        ];
        return finirQcm(`les angles en ${a1} et en ${a2} sont égaux`, dis,
            `<p><b>Que signifient les arcs simples en ${a1} et en ${a2} ?</b></p><p>{{element:0}}</p>`,
            'sens_arcs_losange', elImage(dataUrl(svg)));
    }
    const svg = svgFigure(fig.pts, L, { arcs: [{ sommets: [1, 2], n: 1 }] });
    const dis = [
        { valeur: `il y a un angle droit en ${L[1]} et en ${L[2]}`, code: 'confusion_carre' },
        { valeur: `${L[1]} et ${L[2]} sont les milieux des côtés`, code: 'confusion_milieu' },
        { valeur: 'tous les côtés de la figure ont la même longueur', code: 'confusion_ticks' },
    ];
    return finirQcm(`les angles en ${L[1]} et en ${L[2]} sont égaux`, dis,
        `<p><b>Que signifient les arcs identiques en ${L[1]} et en ${L[2]} ?</b></p><p>{{element:0}}</p>`,
        'sens_arcs_triangle', elImage(dataUrl(svg)));
};

// ─── Format 2 : la LECTURE de la figure ───────────────────────────────────

const genLectureQcm = () => {
    const mode = randomChoice(['cotes', 'cote_de', 'angle_droit', 'angles']);
    if (mode === 'cotes') {
        const forme = randomChoice(['triangle_iso', 'pentagone']);
        const fig = FIGURES[forme]();
        const L = pickLettresSains(LETTRES_POOL, fig.pts.length);
        const paires = forme === 'triangle_iso' ? [[0, 1], [0, 2]] : [[1, 2], [4, 0]];
        const [c1, c2] = paires.map(([i, j]) => `[${L[i]}${L[j]}]`);
        const svg = svgFigure(fig.pts, L, { ticks: [{ paires, n: randomChoice([1, 2]) }] });
        const autres = forme === 'triangle_iso'
            ? [`[${L[0]}${L[1]}] et [${L[1]}${L[2]}]`, `[${L[0]}${L[2]}] et [${L[1]}${L[2]}]`]
            : [`[${L[0]}${L[1]}] et [${L[2]}${L[3]}]`, `[${L[2]}${L[3]}] et [${L[3]}${L[4]}]`];
        const dis = [
            ...autres.map((v) => ({ valeur: v, code: 'autre_paire' })),
            { valeur: 'on ne peut pas le savoir', code: 'refus_du_codage' },
        ];
        return finirQcm(`${c1} et ${c2}`, dis,
            '<p><b>D\'après le codage, quels côtés ont la même longueur ?</b></p><p>{{element:0}}</p>',
            `lecture_cotes_${forme}`, elImage(dataUrl(svg)));
    }
    if (mode === 'cote_de') {
        // le rectangle : deux groupes de traits (simple / double)
        const fig = FIGURES.rectangle();
        const L = pickLettresSains(LETTRES_POOL, 4);
        const svg = svgFigure(fig.pts, L, {
            ticks: [{ paires: [[1, 2], [3, 0]], n: 1 }, { paires: [[0, 1], [2, 3]], n: 2 }],
        });
        const bonne = `[${L[3]}${L[0]}]`;
        const dis = [
            { valeur: `[${L[0]}${L[1]}]`, code: 'cote_adjacent' },
            { valeur: `[${L[2]}${L[3]}]`, code: 'cote_adjacent' },
        ];
        return finirQcm(bonne, dis,
            `<p><b>D'après le codage, quel côté a la même longueur que le côté [${L[1]}${L[2]}] ?</b></p><p>{{element:0}}</p>`,
            'lecture_cote_de_rectangle', elImage(dataUrl(svg)));
    }
    if (mode === 'angle_droit') {
        const forme = randomChoice(['triangle_rect', 'trapeze_rect']);
        const fig = FIGURES[forme]();
        const L = pickLettresSains(LETTRES_POOL, fig.pts.length);
        const svg = svgFigure(fig.pts, L, { carres: fig.droitEn });
        if (forme === 'triangle_rect') {
            const dis = [{ valeur: L[0], code: 'autre_sommet' }, { valeur: L[2], code: 'autre_sommet' }];
            return finirQcm(L[1], dis,
                '<p><b>D\'après le codage, en quel sommet y a-t-il un angle droit ?</b></p><p>{{element:0}}</p>',
                'lecture_angle_droit_triangle', elImage(dataUrl(svg)));
        }
        const bonne = `en ${L[0]} et en ${L[1]}`;
        const dis = [
            { valeur: `en ${L[0]} et en ${L[3]}`, code: 'autre_paire' },
            { valeur: `en ${L[1]} et en ${L[2]}`, code: 'autre_paire' },
            { valeur: `en ${L[2]} et en ${L[3]}`, code: 'autre_paire' },
        ];
        return finirQcm(bonne, dis,
            '<p><b>D\'après le codage, en quels sommets y a-t-il des angles droits ?</b></p><p>{{element:0}}</p>',
            'lecture_angle_droit_trapeze', elImage(dataUrl(svg)));
    }
    // les angles égaux (le triangle isocèle avec ses arcs)
    const fig = FIGURES.triangle_iso();
    const L = pickLettresSains(LETTRES_POOL, 3);
    const svg = svgFigure(fig.pts, L, { arcs: [{ sommets: [1, 2], n: 1 }] });
    const bonne = `les angles en ${L[1]} et en ${L[2]}`;
    const dis = [
        { valeur: `les angles en ${L[0]} et en ${L[1]}`, code: 'autre_paire' },
        { valeur: `les angles en ${L[0]} et en ${L[2]}`, code: 'autre_paire' },
        { valeur: 'on ne peut pas le savoir', code: 'refus_du_codage' },
    ];
    return finirQcm(bonne, dis,
        '<p><b>D\'après le codage, quels angles sont égaux ?</b></p><p>{{element:0}}</p>',
        'lecture_angles', elImage(dataUrl(svg)));
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'TROIS codages fermés ; NOTATIONS écartées ; on DÉCODE seulement ; V/F retiré (arbitrages prof 2026-08-22)',
        'FIGURES VARIÉES (triangles, rectangle, losange, trapèze rectangle, pentagone quelconque), rotations quelconques — la variété teste la lecture, pas la reconnaissance de forme',
        'chaque figure ne porte QUE le codage interrogé (format sens) ; chaque distracteur est FACTUELLEMENT FAUX sur la figure — jeux de distracteurs PAR figure',
        'cohérence dessin ↔ codage par construction (égalités exactes, angles droits exacts, non-codés ≠ d\'au moins ~15-20 %)',
        'notation 6e : un côté nommé est un SEGMENT → crochets [LC], partout (énoncés et choix) — arbitrage prof 2026-08-22',
    ],
    codage_sens_qcm: {
        fixes: { confusion: 'les autres codages + milieu/parallèles/diagonales — choisis figure par figure pour rester factuellement faux' },
    },
    codage_lecture_qcm: {
        fixes: {
            autre_paire: 'les autres paires de côtés/angles/sommets',
            refus_du_codage: '« on ne peut pas le savoir » — faux : le codage tranche',
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    codage_sens_qcm: genSensQcm,
    codage_lecture_qcm: genLectureQcm,
};

const FORMAT_LABELS = {
    codage_sens_qcm: 'Le sens du codage',
    codage_lecture_qcm: 'Lire la figure',
};

export const CATEGORIES = [
    {
        id: 'codages',
        label: L1,
        formatsList: Object.keys(GENERATORS).map((k) => ({ key: k, label: FORMAT_LABELS[k] || k })),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
